import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getEventByBookingLinkId, generateEventTimeSlots, checkEventSlotCapacity, getEventOffice } from '../services/eventService';
import { bookEventAppointmentWithPatient } from '../firebaseBooking';
import Patient from '../models/Patient';
import { hasCompleteAddress } from '../utils/hasCompleteAddress';
import Header from './Header';
import BasicInfo from './BasicInfo';
import Demographics from './Demographics';
import Navigation from './Navigation';

const EventBooking = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [event, setEvent] = useState(null);
  const [office, setOffice] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [basicInfo, setBasicInfo] = useState({});
  const [demographics, setDemographics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [appointmentId, setAppointmentId] = useState(null);
  const [isBasicInfoValid, setIsBasicInfoValid] = useState(false);
  const [patientCheckFunction, setPatientCheckFunction] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifiedPatientId, setVerifiedPatientId] = useState(null);

  const bookingLinkId = searchParams.get('event');

  // Helper function to properly capitalize names
  const formatName = (name) => {
    if (!name) return "";
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [yyyy, mm, dd] = dateString.split("-");
    return `${parseInt(mm, 10)}/${parseInt(dd, 10)}/${yyyy}`;
  };

  useEffect(() => {
    if (bookingLinkId) {
      loadEventData();
    } else {
      setError("No event booking link provided");
      setLoading(false);
    }
  }, [bookingLinkId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch event data
      const eventResult = await getEventByBookingLinkId(bookingLinkId);
      if (!eventResult.success) {
        setError(eventResult.error.message);
        return;
      }

      setEvent(eventResult.event);

      // Fetch office data
      const officeResult = await getEventOffice(eventResult.event.officeId);
      if (!officeResult.success) {
        setError("Office information not found");
        return;
      }

      setOffice(officeResult.office);

      // Generate time slots
      const slots = generateEventTimeSlots(eventResult.event);
      const slotsWithCapacity = await checkEventSlotCapacity(eventResult.event.id, slots);
      setTimeSlots(slotsWithCapacity);

    } catch (err) {
      console.error("Error loading event data:", err);
      setError("Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const handleBasicInfoChange = (updatedInfo) => {
    setBasicInfo(updatedInfo);
  };

  const handleValidityChange = (isValid) => {
    setIsBasicInfoValid(isValid);
  };

  const handlePatientCheckMatches = (data) => {
    if (data.type === 'FUNCTION_REFERENCE') {
      console.log("📝 Received patient check function reference");
      setPatientCheckFunction(data.checkFunction);
    } else if (data.type === 'MATCH_VERIFIED') {
      console.log("👤 Received verified patient match:", data.verifiedPatient);
      
      // Store the verified patient ID
      setVerifiedPatientId(data.verifiedPatient.id);
      
      // Store demographics data to pre-fill the Demographics component
      if (data.demographicsData) {
        console.log("📋 Pre-filling demographics with:", data.demographicsData);
        setDemographics(data.demographicsData);
        setShowVerificationModal(true);
      }
    } else {
      console.warn("⚠️ Received unknown data type in handlePatientCheckMatches");
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Move to basic info step
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Check for existing patients when navigating from BasicInfo to Demographics
      if (patientCheckFunction) {
        console.log("🔍 Checking for existing patients before proceeding to Demographics...");
        try {
          const matches = await patientCheckFunction();
          
          console.log(`✅ Patient check complete, found ${matches.length} potential matches.`);
          
          if (matches.length > 0) {
            console.log("⚠️ Potential duplicate patients found:", matches);
          }
        } catch (error) {
          console.error("❌ Error checking for patient matches:", error);
        }
      }
      
      // Format names and date before creating patient object
      const formattedFirstName = formatName(basicInfo.firstName || "");
      const formattedLastName = formatName(basicInfo.lastName || "");
      const formattedDob = formatDateForDisplay(basicInfo.dob || "");
      
      // Create a patient object with the basic info collected
      const patientData = new Patient({
        firstName: formattedFirstName,
        lastName: formattedLastName,
        email: basicInfo.email || "",
        phone: basicInfo.phone || "",
        address: basicInfo.address || "",
        rawAddress: basicInfo.rawAddress || "",
        city: basicInfo.city || "",
        state: basicInfo.state || "",
        zip: basicInfo.zip || "",
        cityState: basicInfo.cityState || "",
        cityStateZip: basicInfo.cityStateZip || "",
        dob: formattedDob,
        reason: basicInfo.reason || "",
        gender: demographics.gender || "",
        race: demographics.race || "",
        maritalStatus: demographics.maritalStatus || "",
        ethnicity: demographics.ethnicity || "",
        income: demographics.income || "",
        familySize: demographics.familySize || ""
      });
      
      console.log("Patient Basic Info Completed:", patientData);
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Complete the booking
      await completeBooking();
    }
  };

  const completeBooking = async () => {
    try {
      setLoading(true);

      // Create patient data
      const patientData = {
        ...basicInfo,
        ...demographics,
        firstName: formatName(basicInfo.firstName || ""),
        lastName: formatName(basicInfo.lastName || ""),
        dob: formatDateForDisplay(basicInfo.dob || "")
      };

      // Book the event appointment with patient creation
      const bookingResult = await bookEventAppointmentWithPatient(
        event.id,
        event.bookingLinkId,
        patientData,
        selectedSlot,
        verifiedPatientId
      );

      if (bookingResult.success) {
        setAppointmentId(bookingResult.appointmentId);
        setBookingSuccess(true);
        setCurrentStep(4);
      } else {
        setError("Failed to book appointment: " + bookingResult.error.message);
      }
    } catch (err) {
      console.error("Error completing booking:", err);
      setError("Failed to complete booking");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateStep = () => {
    if (currentStep === 1) {
      return selectedSlot !== null;
    } else if (currentStep === 2) {
      return isBasicInfoValid;
    } else if (currentStep === 3) {
      return (
        hasCompleteAddress(demographics) &&
        demographics.gender &&
        demographics.race &&
        demographics.maritalStatus &&
        demographics.ethnicity &&
        demographics.income
      );
    }
    return true;
  };

  if (loading) {
    return (
      <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "1rem", textAlign: "center" }}>
          <div style={{ fontSize: "1.2rem", marginTop: "2rem" }}>Loading event details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "1rem", textAlign: "center" }}>
          <div style={{ fontSize: "1.2rem", marginTop: "2rem", color: "#d32f2f" }}>
            {error}
          </div>
          <div style={{ marginTop: "1rem" }}>
            <a href="/" style={{ color: "#1976d2", textDecoration: "none" }}>
              Return to regular booking
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
        <Header />
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "1rem" }}>
          <div style={{ 
            backgroundColor: "white", 
            padding: "3rem 2rem", 
            borderRadius: "16px", 
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            textAlign: "center",
            border: "1px solid #e0e0e0"
          }}>
            <div style={{ 
              fontSize: "4rem", 
              color: "#4caf50", 
              marginBottom: "1.5rem",
              fontWeight: "bold"
            }}>
              ✓
            </div>
            <h2 style={{ 
              color: "#2e7d32", 
              marginBottom: "2rem", 
              fontSize: "2rem",
              fontWeight: "600"
            }}>
              Booking Confirmed!
            </h2>
            
            <div style={{ 
              backgroundColor: "#f8f9fa",
              padding: "2rem",
              borderRadius: "12px",
              border: "1px solid #e9ecef",
              marginBottom: "2rem"
            }}>
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
                gap: "1.5rem",
                textAlign: "left"
              }}>
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>EVENT</div>
                  <div style={{ fontSize: "1.1rem", color: "#333", fontWeight: "600" }}>{event.eventName}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>DATE & TIME</div>
                  <div style={{ fontSize: "1.1rem", color: "#333", fontWeight: "600" }}>
                    {selectedSlot.startTime.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric' 
                    })} at {selectedSlot.time}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>LOCATION</div>
                  <div style={{ fontSize: "1.1rem", color: "#333", fontWeight: "600" }}>{office.name}</div>
                </div>
                <div>
                  <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500", marginBottom: "0.5rem" }}>PATIENT</div>
                  <div style={{ fontSize: "1.1rem", color: "#333", fontWeight: "600" }}>
                    {basicInfo.firstName} {basicInfo.lastName}
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: "#e8f5e8",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #c8e6c9"
            }}>
              <div style={{ 
                fontSize: "1rem", 
                color: "#2e7d32", 
                fontWeight: "500"
              }}>
                📧 You will receive a confirmation email shortly.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Quicksand", backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <Header />
      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "1rem" }}>
        {/* Event Information */}
        <div style={{ 
          backgroundColor: "white", 
          padding: "2rem", 
          borderRadius: "12px", 
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          marginBottom: "1.5rem",
          border: "1px solid #e0e0e0"
        }}>
          <h2 style={{ 
            color: "#1976d2", 
            marginBottom: "0.75rem", 
            fontSize: "1.75rem",
            fontWeight: "600"
          }}>
            {event.eventName}
          </h2>
          <p style={{ 
            color: "#666", 
            marginBottom: "1.5rem", 
            fontSize: "1.1rem",
            lineHeight: "1.5"
          }}>
            {event.eventDescription}
          </p>
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "1rem",
            padding: "1rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ 
                width: "8px", 
                height: "8px", 
                backgroundColor: "#1976d2", 
                borderRadius: "50%", 
                marginRight: "0.75rem" 
              }}></div>
              <div>
                <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500" }}>DATE</div>
                <div style={{ fontSize: "1rem", color: "#333", fontWeight: "600" }}>
                  {event.startDateTime.toDate().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ 
                width: "8px", 
                height: "8px", 
                backgroundColor: "#1976d2", 
                borderRadius: "50%", 
                marginRight: "0.75rem" 
              }}></div>
              <div>
                <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500" }}>TIME</div>
                <div style={{ fontSize: "1rem", color: "#333", fontWeight: "600" }}>
                  {event.startDateTime.toDate().toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })} - {event.endDateTime.toDate().toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ 
                width: "8px", 
                height: "8px", 
                backgroundColor: "#1976d2", 
                borderRadius: "50%", 
                marginRight: "0.75rem" 
              }}></div>
              <div>
                <div style={{ fontSize: "0.85rem", color: "#666", fontWeight: "500" }}>LOCATION</div>
                <div style={{ fontSize: "1rem", color: "#333", fontWeight: "600" }}>
                  {office.name}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Time Slot Selection */}
        {currentStep === 1 && (
          <div
            style={{
              backgroundColor: "white",
              padding: "1.5rem",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
            }}
          >
            <h3
              style={{
                marginBottom: "1.5rem",
                color: "#273146",
                fontSize: "1.25rem",
                fontWeight: 700,
                fontFamily: "inherit"
              }}
            >
              Select a Time Slot
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "0.9rem",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
               {timeSlots.map((slot) => {
                 // Compute button appearance based on selection and availability
                 const isSelected = selectedSlot?.id === slot.id;
                 const isAvailable = slot.available;
                 const isFull = !slot.available;

                 // Newer, more consistent palette
                 const base = "#1976d2";
                 const baseLight = "#e3f2fd";
                 const borderColor = isSelected
                   ? base
                   : !isAvailable
                   ? "#e0e0e0"
                   : "#ebeef2";
                 const bgColor =
                   isSelected
                     ? baseLight
                     : isFull
                     ? "#f5f6fa"
                     : "#fff";
                 const fontColor = isAvailable ? "#222b45" : "#b0b7c3";

                 return (
                   <button
                     key={slot.id}
                     disabled={!isAvailable}
                     onClick={() => handleSlotSelect(slot)}
                     onMouseEnter={(e) => {
                       if (isAvailable && !isSelected) {
                         e.target.style.backgroundColor = "#f0f4fa";
                         e.target.style.borderColor = base;
                         e.target.style.boxShadow = "0 3px 10px rgba(25,118,210,0.10)";
                       }
                     }}
                     onMouseLeave={(e) => {
                       if (isAvailable && !isSelected) {
                         e.target.style.backgroundColor = bgColor;
                         e.target.style.borderColor = borderColor;
                         e.target.style.boxShadow = "0 1px 4px rgba(20,40,80,0.04)";
                       }
                     }}
                     style={{
                       width: "100%",
                       padding: "1.1rem 0.75rem",
                       border: `2px solid ${borderColor}`,
                       outline: "none",
                       borderRadius: "8px",
                       background: bgColor,
                       color: fontColor,
                       cursor: isAvailable ? "pointer" : "not-allowed",
                       fontSize: "1.08rem",
                       fontFamily: "inherit",
                       fontWeight: isSelected ? 700 : 500,
                       transition:
                         "background 0.15s, border-color 0.18s, color 0.18s, box-shadow 0.2s",
                       boxShadow: isSelected
                         ? "0 2px 8px rgba(25,118,210,0.08)"
                         : "0 1px 4px rgba(20,40,80,0.04)",
                       display: "flex",
                       flexDirection: "column",
                       alignItems: "center",
                       justifyContent: "center",
                       minHeight: "63px",
                       letterSpacing: "0"
                     }}
                   >
                     <div
                       style={{
                         fontSize: "1.13rem",
                         marginBottom: "0.12rem",
                         color: isSelected ? base : fontColor,
                         fontFamily: "inherit",
                         fontWeight: isSelected ? 700 : 500,
                         letterSpacing: "-0.5px"
                       }}
                     >
                       {slot.time}
                     </div>
                     {!isAvailable && (
                       <div
                         style={{
                           color: "#d32f2f",
                           fontSize: "0.8rem",
                           fontWeight: 600,
                           fontFamily: "inherit",
                           textTransform: "uppercase",
                           letterSpacing: "0.7px",
                         }}
                       >
                         Full
                       </div>
                     )}
                     {isAvailable && isSelected && (
                       <div
                         style={{
                           fontSize: "0.8rem",
                           color: base,
                           marginTop: "0.22rem",
                           fontWeight: 700,
                           letterSpacing: "0.02em",
                           fontFamily: "inherit"
                         }}
                       >
                         Selected
                       </div>
                     )}
                     {isAvailable && !isSelected && (
                       <div
                         style={{
                           fontSize: "0.74rem",
                           color: "#586175",
                           fontWeight: 500,
                           marginTop: "0.18rem",
                           fontFamily: "inherit"
                         }}
                       >
                         Available
                       </div>
                     )}
                   </button>
                 );
               })}
            </div>
            {timeSlots.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#586175",
                  fontFamily: "inherit",
                  fontSize: "1.03rem",
                  fontWeight: 500,
                }}
              >
                No time slots available for this event.
              </div>
            )}
          </div>
        )}

        {/* Step 2: Basic Information */}
        {currentStep === 2 && (
          <BasicInfo 
            basicInfo={basicInfo} 
            onChange={handleBasicInfoChange} 
            onValidityChange={handleValidityChange} 
            onCheckPatientMatches={handlePatientCheckMatches}
          />
        )}

        {/* Step 3: Demographics */}
        {currentStep === 3 && (
          <Demographics 
            demographics={demographics} 
            onChange={setDemographics} 
            showVerificationModal={showVerificationModal}
            onVerificationModalClose={() => setShowVerificationModal(false)}
          />
        )}

        {/* Navigation */}
        <Navigation 
          currentStep={currentStep} 
          onBack={handleBack} 
          onNext={handleNext}
          maxStep={3}
          nextDisabled={!validateStep()}
          nextText={currentStep === 3 ? "Complete Booking" : "Next"}
        />
      </div>
    </div>
  );
};

export default EventBooking;
